import { townColorHex as hex, shadeTownColor as darken } from './townPropTemplates';
import type { TownPropPalette, TownHouseTemplate } from './townPropTemplates';

/** Original cottage SVG, retained byte-for-byte when no named template is selected. */
export function villageHouseBody(wall: number): string {
  const w = hex(wall);
  const wDark = hex(darken(wall, 0.72));
  return `
    <defs>
      <linearGradient id="hw" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${w}" />
        <stop offset="1" stop-color="${wDark}" />
      </linearGradient>
      <linearGradient id="hr" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#6a4a32" />
        <stop offset="1" stop-color="#4a3020" />
      </linearGradient>
    </defs>
    <path d="M62 38 q-2 -8 3 -13 q6 -5 3 -12" fill="none" stroke="#a0a0a0"
      stroke-width="4" stroke-linecap="round" opacity="0.3" />
    <rect x="58" y="24" width="10" height="20" fill="${hex(darken(wall, 0.55))}" />
    <rect x="18" y="52" width="54" height="48" fill="url(#hw)" />
    <path d="M8 56 L45 14 L82 56 Z" fill="url(#hr)" />
    <rect x="8" y="53" width="74" height="5" rx="2" fill="#4a3020" />
    <rect x="39" y="72" width="13" height="28" rx="2" fill="#4a3020" />
    <circle cx="49" cy="87" r="1.4" fill="#a87838" />
    <rect x="23" y="61" width="11" height="11" rx="1.5" fill="#ffcc00" />
    <rect x="56" y="61" width="11" height="11" rx="1.5" fill="#ffcc00" />
    <path d="M28.5 61 v11 M23 66.5 h11 M61.5 61 v11 M56 66.5 h11"
      stroke="#4a3020" stroke-width="1.2" opacity="0.8" />`;
}

/** Three complete building silhouettes in the existing 90 × 100 bottom-anchored frame. */
export function buildTownHouseBody(template: TownHouseTemplate, p: TownPropPalette): string {
  const colors = Object.fromEntries(Object.entries(p).map(([key, value]) => [key, hex(value)])) as Record<keyof typeof p, string>;
  const window = (x: number, y: number, w = 11, h = 14) => `
    <rect x="${x - 2}" y="${y - 2}" width="${w + 4}" height="${h + 4}" fill="${colors.wood}" />
    <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${colors.glass}" />
    <path d="M${x + w / 2} ${y}v${h} M${x} ${y + h * 0.45}h${w}" stroke="${colors.wood}" stroke-width="1.3" />
    <path d="M${x} ${y + h}h${w}" stroke="${colors.glassCore}" stroke-width="1" />`;
  const door = (x: number, y: number, w = 14) => `
    <path d="M${x} 100V${y + 5}q${w / 2} -9 ${w} 0V100Z" fill="${colors.wood}" />
    <path d="M${x + 3} 99V${y + 7}" stroke="${colors.woodEdge}" stroke-width="1" opacity="0.7" />
    <circle cx="${x + w - 3}" cy="${y + 17}" r="1.2" fill="${colors.glass}" />`;
  const frame = (left: number, top: number, width: number) => `
    <rect x="${left}" y="${top}" width="${width}" height="${100 - top}" fill="${colors.primary}" />
    <rect x="${left}" y="${top}" width="${width * 0.16}" height="${100 - top}" fill="${colors.shade}" />
    <rect x="${left}" y="92" width="${width}" height="8" fill="${colors.stone}" />
    <path d="M${left} 96h${width} M${left + 15} 92v4 M${left + 36} 96v4 M${left + width - 12} 92v4" stroke="${colors.iron}" stroke-width="1" opacity="0.6" />`;
  if (template === 'gable_inn') return `
    ${frame(11, 44, 68)}
    <rect x="65" y="15" width="9" height="26" fill="${colors.stone}" />
    <rect x="63" y="13" width="13" height="4" fill="${colors.iron}" />
    <path d="M3 48L45 7L87 48Z" fill="${colors.roof}" />
    <path d="M3 48L45 7L87 48 M14 42h62 M22 34h46 M30 26h30" fill="none" stroke="${colors.roofEdge}" stroke-width="1.4" />
    <path d="M11 48h68 M11 73h68 M16 48v44 M74 48v44 M16 73l20 19 M74 73L54 92" fill="none" stroke="${colors.wood}" stroke-width="3" />
    ${window(24, 53)} ${window(55, 53)} ${door(38, 78)}
    <path d="M42 29h7v9h-7Z" fill="${colors.glass}" />
    <path d="M16 76h14v12H16Z" fill="${colors.wood}" />
    <path d="M19 82h8 M23 79v6" stroke="${colors.woodEdge}" stroke-width="1.5" />`;
  if (template === 'cross_gable_house') return `
    ${frame(8, 43, 74)}
    <path d="M1 49L27 21L52 49Z M31 49L59 8L89 49Z" fill="${colors.roof}" />
    <path d="M1 49L27 21L52 49 M31 49L59 8L89 49 M9 42h35 M41 37h37 M47 28h24" fill="none" stroke="${colors.roofEdge}" stroke-width="1.4" />
    <path d="M8 48h74 M8 76h74 M12 49v43 M79 49v43 M47 49v43" stroke="${colors.wood}" stroke-width="3" />
    ${window(19, 56, 15, 13)} ${window(59, 55, 12, 15)} ${door(25, 81, 15)}
    <path d="M52 79h23l-2 9H54Z" fill="${colors.wood}" />
    <path d="M53 80h21 M57 79v8 M68 79v8" stroke="${colors.woodEdge}" stroke-width="1.2" />
    <path d="M55 28h8v10h-8Z" fill="${colors.glass}" />`;
  return `
    ${frame(7, 37, 76)}
    <path d="M1 41L18 17H72L89 41Z" fill="${colors.roof}" />
    <path d="M1 41L18 17H72L89 41 M8 34h74 M14 26h62" fill="none" stroke="${colors.roofEdge}" stroke-width="1.5" />
    <path d="M29 39L45 20L61 39Z" fill="${colors.primary}" stroke="${colors.wood}" stroke-width="2" />
    <path d="M42 29h6v8h-6Z" fill="${colors.glass}" />
    <path d="M7 42h76 M7 72h76 M12 42v50 M78 42v50 M34 42v30 M57 42v30 M12 73l19 19 M78 73L59 92" fill="none" stroke="${colors.wood}" stroke-width="3.5" />
    ${window(18, 51, 10, 14)} ${window(40, 51, 10, 14)} ${window(62, 51, 10, 14)} ${door(36, 79, 18)}
    <path d="M30 78h30v4H30Z" fill="${colors.woodEdge}" />`;
}
